import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Notifications as NotificationsIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  LocationOn as LocationIcon,
  Public as PublicIcon,
  Speed as SpeedIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const AlertResponseComponentDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/ift-rvss-system');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to IFT/RVSS System"
        >
          Back to IFT/RVSS
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            Alert and Response System
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Real-Time Notification and Coordination Platform
          </Typography>
        </Box>
      </Box>

      {/* Component Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Component Overview
          </Typography>
          <Typography variant="body1" paragraph>
            The Alert and Response System is the critical communication layer that transforms sensor detections into 
            actionable intelligence for Border Patrol agents. When the Sensor Integration Suite identifies a high-confidence 
            threat, the Alert and Response System instantly notifies the appropriate personnel, provides precise GPS 
            coordinates, transmits video clips, assesses threat priority, and coordinates multi-agency response. This 
            automated workflow ensures rapid agent deployment to the exact location of border incursions, maximizing 
            interdiction success while minimizing response time.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Component Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="Alert and Response System 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is Alert and Response System"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    A <strong>real-time notification and coordination platform</strong> that automatically generates 
                    <strong> alerts from sensor detections, distributes notifications to appropriate personnel, provides 
                    GPS coordinates and video evidence, assesses threat priority, and coordinates multi-agency response</strong>. 
                    Integrates with <strong>mobile devices, radio systems, operations centers, and field agent equipment</strong> 
                    for seamless communication.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who uses Alert and Response System"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Primary users are <strong>Border Patrol field agents receiving alerts, surveillance operators at sector 
                    operations centers, tactical coordinators managing response, and supervisors monitoring operations</strong>. 
                    Also supports <strong>multi-agency partners (ICE, DEA, local law enforcement)</strong> and <strong>air 
                    and marine operations</strong> for coordinated interdiction efforts.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was Alert and Response System deployed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Initial alert systems deployed in <strong>2008-2010</strong> with basic notification capabilities. Major 
                    enhancement in <strong>2013-2015</strong> with mobile device integration and GPS coordination. 
                    <strong> 2017-2019</strong> added priority classification and multi-agency coordination. <strong>2020-present</strong> 
                    integration with AI analytics and predictive routing. Operates <strong>24/7/365</strong> with 
                    <strong> sub-second alert generation and distribution</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is Alert and Response System deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>Central processing</strong> at sector operations centers with <strong>distributed alert delivery</strong> 
                    to field agents across all <strong>9 Border Patrol sectors</strong>. Integrates with <strong>mobile devices, 
                    vehicle-mounted systems, and portable radios</strong> used by agents in the field. Coordination with 
                    <strong> air operations, marine units, and partner agency command centers</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why Alert and Response System exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To <strong>minimize response time from detection to interdiction</strong> by automating alert generation 
                    and distribution. Without automated alerts, <strong>sensor detections would require manual monitoring and 
                    radio coordination</strong>, adding critical minutes to response time. Enables <strong>rapid agent deployment 
                    to precise GPS locations with full situational awareness</strong>, dramatically increasing <strong>apprehension 
                    rates and border security effectiveness</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Alert Components */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Alert System Components
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <NotificationsIcon sx={{ fontSize: 40, color: '#D32F2F', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                  Automated Alert Generation
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Instant notification creation from sensor detections.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Real-Time Processing:</strong> Sub-second alert generation from detection</Typography></li>
                <li><Typography variant="body2"><strong>Confidence Thresholds:</strong> Only high-confidence detections trigger alerts</Typography></li>
                <li><Typography variant="body2"><strong>Alert Metadata:</strong> Detection time, location, sensor type, classification</Typography></li>
                <li><Typography variant="body2"><strong>Video Attachment:</strong> Automatic capture and transmission of video clips</Typography></li>
                <li><Typography variant="body2"><strong>Historical Context:</strong> Recent activity in the area included</Typography></li>
                <li><Typography variant="body2"><strong>Duplicate Suppression:</strong> Prevents multiple alerts for same event</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  GPS Coordination
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Precise location data for rapid agent response.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Precise Coordinates:</strong> GPS location accurate to within 10 meters</Typography></li>
                <li><Typography variant="body2"><strong>Map Integration:</strong> Alert location displayed on GIS maps</Typography></li>
                <li><Typography variant="body2"><strong>Routing Guidance:</strong> Optimal path to detection site</Typography></li>
                <li><Typography variant="body2"><strong>Terrain Information:</strong> Elevation, obstacles, access routes</Typography></li>
                <li><Typography variant="body2"><strong>Agent Proximity:</strong> Identifies nearest available agents</Typography></li>
                <li><Typography variant="body2"><strong>Waypoint Sharing:</strong> Coordinates sent to vehicle navigation systems</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Priority Classification
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Threat assessment and resource allocation prioritization.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Priority Levels:</strong> Critical, High, Medium, Low classification</Typography></li>
                <li><Typography variant="body2"><strong>Threat Assessment:</strong> Group size, speed, direction, behavior</Typography></li>
                <li><Typography variant="body2"><strong>Resource Matching:</strong> Appropriate response level (1 agent vs team)</Typography></li>
                <li><Typography variant="body2"><strong>Escalation Rules:</strong> Automatic elevation for high-threat scenarios</Typography></li>
                <li><Typography variant="body2"><strong>Historical Analysis:</strong> Known smuggling patterns influence priority</Typography></li>
                <li><Typography variant="body2"><strong>Dynamic Adjustment:</strong> Priority updates as situation evolves</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <GroupIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Multi-Agency Coordination
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Seamless communication with partner agencies.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Partner Notifications:</strong> Alerts shared with ICE, DEA, local law enforcement</Typography></li>
                <li><Typography variant="body2"><strong>Air Support:</strong> Helicopter and drone dispatch coordination</Typography></li>
                <li><Typography variant="body2"><strong>Marine Units:</strong> Coastal and riverine response integration</Typography></li>
                <li><Typography variant="body2"><strong>Joint Operations:</strong> Coordinated multi-agency responses</Typography></li>
                <li><Typography variant="body2"><strong>Information Sharing:</strong> Real-time intelligence distribution</Typography></li>
                <li><Typography variant="body2"><strong>Unified Command:</strong> Integrated operations center coordination</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alert Workflow */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SpeedIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Alert Workflow Timeline
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#003366' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Time</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Action</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>System</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Result</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>T+0 sec</strong></TableCell>
              <TableCell>Sensor detection</TableCell>
              <TableCell>Ground sensor/radar/camera</TableCell>
              <TableCell>Movement detected</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>T+0.5 sec</strong></TableCell>
              <TableCell>Sensor fusion analysis</TableCell>
              <TableCell>Integration Suite</TableCell>
              <TableCell>High-confidence classification</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>T+1 sec</strong></TableCell>
              <TableCell>Alert generation</TableCell>
              <TableCell>Alert System</TableCell>
              <TableCell>Alert created with GPS, video, priority</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>T+2 sec</strong></TableCell>
              <TableCell>Alert distribution</TableCell>
              <TableCell>Communication network</TableCell>
              <TableCell>Notifications sent to agents</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>T+5 sec</strong></TableCell>
              <TableCell>Agent acknowledgment</TableCell>
              <TableCell>Mobile device/radio</TableCell>
              <TableCell>Agent accepts assignment</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>T+2-5 min</strong></TableCell>
              <TableCell>Agent arrival</TableCell>
              <TableCell>Field response</TableCell>
              <TableCell>Agent reaches detection location</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* System Scale */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Scale & Performance
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                50K+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Annual Alerts
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                High-confidence detections
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                &lt;2sec
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Alert Delivery
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Detection to notification
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                2-5min
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Response Time
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Average agent arrival
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                85%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Apprehension Rate
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Successful interdictions
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Operational Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Operational Impact
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Rapid Response:</strong> Sub-2-second alert delivery to agents</Typography></li>
                <li><Typography variant="body2"><strong>Precise Location:</strong> GPS coordinates enable direct navigation</Typography></li>
                <li><Typography variant="body2"><strong>Situational Awareness:</strong> Video and context provided with alerts</Typography></li>
                <li><Typography variant="body2"><strong>Priority Routing:</strong> High-threat alerts get immediate attention</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Multi-Agency Coordination:</strong> Seamless partner integration</Typography></li>
                <li><Typography variant="body2"><strong>Resource Optimization:</strong> Right response for each threat level</Typography></li>
                <li><Typography variant="body2"><strong>Evidence Collection:</strong> Automatic video capture for prosecutions</Typography></li>
                <li><Typography variant="body2"><strong>Higher Apprehensions:</strong> 85% success rate with automated alerts</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Integration Points */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            System Integrations
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Chip label="Sensor Integration Suite" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Detection input source</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Operations Centers" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Central monitoring</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Mobile Devices" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Agent smartphones/tablets</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Radio Systems" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Voice communications</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Vehicle Systems" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">In-vehicle displays</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="GIS Mapping" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Geographic visualization</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Partner Agencies" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">ICE, DEA, local police</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Air/Marine Ops" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Aviation and maritime</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AlertResponseComponentDetail;
